/**
 * GravityScarPassiveSkillEffect — Astral dense-path passive: gravity scar
 * zones on Meteo kills.
 *
 * When this passive unlocks, it registers a death trigger via
 * DeathTriggerSystem: any enemy killed by the Meteo skill spawns a
 * `gravity_scar` zone at the death position. The zone lasts 8000ms,
 * covers 1 tile radius, and applies a 30% slow (speedMultiplier 0.7).
 *
 * The zone slow is a ZoneSystem concern — this passive carries
 * `scarSlowMultiplier` (0.3 → 0.7 speed) as configuration for
 * integration-time zone creation. The DeathTriggerSystem event tells
 * the runtime where and what to spawn; the zone type's speed multiplier
 * comes from this passive's config.
 *
 * Apotheosis parent: one of the two OR-gate parents for
 * `apotheosis_meteo` (Orbital Bombardment).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § meteo_passive
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { DeathTriggerConfig } from '../../../systems/DeathTriggerSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface GravityScarDeps {
  readonly deathTriggers: {
    register(config: DeathTriggerConfig): void;
    unregister(triggerId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface GravityScarConfig {
  /** Gravity scar zone lifetime in ms (8000). */
  readonly scarDurationMs: number;
  /** Gravity scar zone radius in tiles (1). */
  readonly scarRadiusTiles: number;
  /** Speed reduction fraction (0.3 = 30% slow → 0.7 speed multiplier). */
  readonly scarSlowMultiplier: number;
}

const DEFAULT_CONFIG: GravityScarConfig = {
  scarDurationMs: 8000,
  scarRadiusTiles: 1,
  scarSlowMultiplier: 0.3,
};

const TRIGGER_ID = 'gravity_scar_trigger';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class GravityScarPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'meteo_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: GravityScarDeps;
  readonly config: GravityScarConfig;

  private _triggerRegistered = false;

  constructor(deps: GravityScarDeps, config: Partial<GravityScarConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this._registerTrigger();
  }

  // --- State getters ---

  get triggerRegistered(): boolean { return this._triggerRegistered; }

  /**
   * Speed multiplier for the gravity_scar zone (1 - scarSlowMultiplier).
   * Integration code uses this when translating the DeathTriggerSystem
   * spawnZone event into a ZoneSystem.create() call.
   */
  get zoneSpeedMultiplier(): number { return 1 - this.config.scarSlowMultiplier; }

  // ---------------------------------------------------------------------------
  // Internal — trigger lifecycle
  // ---------------------------------------------------------------------------

  private _registerTrigger(): void {
    this.deps.deathTriggers.register({
      id: TRIGGER_ID,
      sourceSkillId: 'meteo',
      requireKillBySkill: ['meteo', 'apotheosis_meteo'],
      spawnZone: {
        zoneType: 'gravity_scar',
        radiusTiles: this.config.scarRadiusTiles,
        durationMs: this.config.scarDurationMs,
      },
    });
    this._triggerRegistered = true;
  }

  // ---------------------------------------------------------------------------
  // SkillEffect interface
  // ---------------------------------------------------------------------------

  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  update(_delta: number): void { /* passive — nothing to tick */ }

  destroy(): void {
    if (this._triggerRegistered) {
      this.deps.deathTriggers.unregister(TRIGGER_ID);
      this._triggerRegistered = false;
    }
  }
}
